import { Injectable, inject } from '@angular/core';
import {
  OrderFinancialTransactionRequestDto,
  OrderItemPaymentItemResponseDto,
  OrderItemPaymentResponseDto,
  PaymentModeResponseDto,
  PaymentOperationTypeEnumResponseDto,
} from '@core/services/apiclient';
import { SalesPaymentModeModel } from '@shared/models/sales-payment-mode.model';
import { OrdersCancelService } from './orders-cancel.service';
import { PaymentTransactionService } from './payment-transaction.service';

@Injectable({
  providedIn: 'root',
})
export class RefundPaymentModeService {
  private readonly ordersCancelService = inject(OrdersCancelService);
  private readonly paymentTransactionService = inject(PaymentTransactionService);

  processOrderPayments(
    allOrderPayments: OrderItemPaymentResponseDto[],
    paymentModes: PaymentModeResponseDto[],
    totalAmount: number,
  ): {
    availableModes: PaymentModeResponseDto[];
    refundsPayload: OrderFinancialTransactionRequestDto[];
    cancelPaymentModes: SalesPaymentModeModel[];
    isPaymentBreakdown: boolean;
  } {
    if (allOrderPayments?.length === 0) {
      return this.paymentTransactionService.createFreeModeResult(paymentModes, totalAmount);
    }

    const paymentItems = (allOrderPayments || []).filter(
      ({ operationType }) => operationType === PaymentOperationTypeEnumResponseDto.Payment,
    );

    if (paymentItems.length === 0) {
      return this.paymentTransactionService.createFreeModeResult(paymentModes, totalAmount);
    }

    const paybackAmount = this.ordersCancelService.getPaybackAmount(allOrderPayments);
    const orderPayments = paymentItems
      .map(({ items }) => items || [])
      .filter((items) => items.length > 0)
      .reduce((acc, curr) => [...acc, ...curr], [] as OrderItemPaymentItemResponseDto[]);

    if (orderPayments.length === 0) {
      return this.paymentTransactionService.createFreeModeResult(paymentModes, totalAmount);
    }

    return this.getPaymentModesWithoutPayback(paybackAmount, orderPayments, paymentModes, totalAmount);
  }

  private getPaymentModesWithoutPayback(
    paybackAmount: number,
    orderPayments: OrderItemPaymentItemResponseDto[],
    paymentModeitems: PaymentModeResponseDto[],
    totalAmount: number,
  ): {
    availableModes: PaymentModeResponseDto[];
    refundsPayload: OrderFinancialTransactionRequestDto[];
    cancelPaymentModes: SalesPaymentModeModel[];
    isPaymentBreakdown: boolean;
  } {
    const refundsPayload: OrderFinancialTransactionRequestDto[] = [];
    const cancelPaymentModes: SalesPaymentModeModel[] = [];
    const modes: PaymentModeResponseDto[] = [];
    let remainingPaybackAmount = paybackAmount;

    orderPayments.forEach((paymentMode) => {
      let amount = 0;
      [amount, remainingPaybackAmount] = this.ordersCancelService.CalculateCurrentAmount(
        paymentMode,
        remainingPaybackAmount,
      );
      const mode = this.ordersCancelService.getPaymentMode(paymentMode?.mode?.id, paymentModeitems);
      if (mode && !modes.some((item) => item.id === mode.id)) {
        modes.push(mode);
      }
      if (amount > 0 && mode) {
        const refundPayload = this.paymentTransactionService.createRefundPayload(mode, amount);
        refundPayload.paymentId = paymentMode.paymentId ?? -1;
        refundsPayload.push(refundPayload);
        cancelPaymentModes.push(
          this.ordersCancelService.mapOrderPaymentToPaymentMode(paymentMode, paymentModeitems, amount),
        );
      }
    });

    const isPaymentBreakdown = refundsPayload.length > 1;

    if (!modes.length) {
      return this.paymentTransactionService.createFreeModeResult(paymentModeitems, totalAmount);
    }

    if (refundsPayload.length === 0 && modes.length > 0) {
      const defaultMode = modes[0];
      refundsPayload.push(this.paymentTransactionService.createRefundPayload(defaultMode, totalAmount));
      cancelPaymentModes.push(this.paymentTransactionService.createPaymentMode(defaultMode, totalAmount));
    }

    return {
      availableModes: paymentModeitems,
      refundsPayload,
      cancelPaymentModes,
      isPaymentBreakdown,
    };
  }
}
